import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { Progress } from '../ui/progress'
import { Loader2, Crown, Sparkles, TrendingUp, Wallet, Plus } from 'lucide-react'
import { apiClient } from '../../lib/api'
import type { UserMembership, MembershipLimits } from '../../types'
import { MembershipUpgradeDialog } from './MembershipUpgradeDialog'
import { CreditPurchaseDialog } from './CreditPurchaseDialog'

export function MembershipCard() {
  const [membership, setMembership] = useState<UserMembership | null>(null)
  const [limits, setLimits] = useState<MembershipLimits | null>(null)
  const [loading, setLoading] = useState(true)
  const [showUpgrade, setShowUpgrade] = useState(false)
  const [showCreditPurchase, setShowCreditPurchase] = useState(false)

  useEffect(() => {
    loadMembership()
  }, [])

  const loadMembership = async () => {
    try {
      setLoading(true)
      const [membershipRes, limitsRes] = await Promise.all([
        apiClient.membership.getMembership(),
        apiClient.membership.getMembershipLimits(),
      ])

      if (membershipRes.success) {
        setMembership(membershipRes.membership)
      }

      if (limitsRes.success) {
        setLimits(limitsRes.limits)
      }
    } catch (error) {
      console.error('Error loading membership:', error)
    } finally {
      setLoading(false)
    }
  }

  const getTierIcon = (tier: string) => {
    switch (tier) {
      case 'book_giant':
        return <Crown className="h-5 w-5 text-yellow-500" />
      case 'plus':
        return <Sparkles className="h-5 w-5 text-purple-500" />
      default:
        return <TrendingUp className="h-5 w-5 text-blue-500" />
    }
  }

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'book_giant':
        return 'bg-gradient-to-r from-yellow-500 to-orange-500'
      case 'plus':
        return 'bg-gradient-to-r from-purple-500 to-pink-500'
      default:
        return 'bg-gradient-to-r from-blue-500 to-cyan-500'
    }
  }

  if (loading) {
    return (
      <Card className="membership-card">
        <CardContent className="flex items-center justify-center p-8">
          <Loader2 className="h-8 w-8 animate-spin" />
        </CardContent>
      </Card>
    )
  }

  if (!membership || !limits) {
    return (
      <Card className="membership-card">
        <CardContent className="p-8">
          <p className="text-center text-muted-foreground">Failed to load membership information</p>
        </CardContent>
      </Card>
    )
  }

  const downloadsPercentage = (limits.downloads.used / limits.downloads.limit) * 100

  return (
    <>
      <Card className="membership-card">
        <CardHeader className={`text-white ${getTierColor(membership.tier)}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {getTierIcon(membership.tier)}
              <div>
                <CardTitle className="text-white">{membership.tier_name || membership.tier}</CardTitle>
                <CardDescription className="text-white/80">
                  {membership.tier === 'basic' ? 'Free' : `$${membership.tier_price}/month`}
                </CardDescription>
              </div>
            </div>
            {membership.tier !== 'book_giant' && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowUpgrade(true)}
              >
                Upgrade
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-6 space-y-6">
          {/* Credit Balance */}
          <div className="bg-muted/50 rounded-lg p-4 space-y-3">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Wallet className="h-4 w-4 text-primary" />
                <span className="font-medium">Credit Balance</span>
              </div>
              <span className="text-2xl font-bold text-primary">
                ${(membership?.credit_balance_usd || 0).toFixed(2)}
              </span>
            </div>
            <Button 
              onClick={() => setShowCreditPurchase(true)} 
              size="sm" 
              className="w-full"
              variant="outline"
            >
              <Plus className="h-4 w-4 mr-2" />
              Buy Credits
            </Button>
            <p className="text-xs text-muted-foreground">
              Credits are used for translations
            </p>
          </div>

          {/* Book Takes Usage */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="font-medium">Book Takes</span>
              <span className="text-muted-foreground">
                {limits.downloads.remaining} / {limits.downloads.limit} remaining
              </span>
            </div>
            <Progress value={downloadsPercentage} className="h-2" />
            <p className="text-xs text-muted-foreground">
              {limits.downloads.used} takes used this month
            </p>
          </div>

          {/* Discount Badge */}
          {limits.discount_percent > 0 && (
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="text-sm">
                {limits.discount_percent}% discount on translations
              </Badge>
            </div>
          )}

          {/* Billing Cycle */}
          {limits.billing_cycle_end && (
            <p className="text-xs text-muted-foreground">
              Current billing cycle ends on{' '}
              {new Date(limits.billing_cycle_end).toLocaleDateString()}
            </p>
          )}
        </CardContent>
      </Card>

      {showUpgrade && (
        <MembershipUpgradeDialog
          currentTier={membership.tier}
          onClose={() => setShowUpgrade(false)}
          onUpgraded={loadMembership}
        />
      )}

      <CreditPurchaseDialog
        open={showCreditPurchase}
        onOpenChange={setShowCreditPurchase}
        onPurchaseComplete={loadMembership}
      />
    </>
  )
}

